import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, ArrowLeft, CheckCircle2, RefreshCw } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { useTechAuth } from '@/contexts/TechAuthContext';
import { useStax } from '@/hooks/useStax';
import {
  createTechDiagnosticInvoice,
  getTechDiagnosticSession,
  payTechDiagnosticInvoice,
  type TechDiagnosticInvoice,
  type TechDiagnosticSession,
} from '@/lib/tech-api-client';

type PresentationOptionId = 'option_1' | 'option_2' | 'option_3' | 'option_4';

type SelectedOptionSummary = {
  id: PresentationOptionId;
  title: string;
  totalDueTodayUsd: number | null;
  membershipFirstPaymentUsd: number | null;
  notes: string;
};

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error || 'Unknown error');
}

function toNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function formatUsd(value: number | null): string {
  if (value === null) return '—';
  return `$${value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function formatUsdFromCents(cents: number | null | undefined): string {
  const n = Number(cents || 0);
  return `$${(n / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function normalizeOptionId(raw: unknown): PresentationOptionId | '' {
  const normalized = String(raw || '').trim().toLowerCase();
  if (normalized === 'good' || normalized === 'option_1' || normalized === 'option1') return 'option_1';
  if (normalized === 'better' || normalized === 'option_2' || normalized === 'option2') return 'option_2';
  if (normalized === 'best' || normalized === 'option_3' || normalized === 'option3') return 'option_3';
  if (normalized === 'replacement' || normalized === 'option_4' || normalized === 'option4') return 'option_4';
  return '';
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function getStringFrom(obj: unknown, ...keys: string[]): string {
  const rec = asRecord(obj);
  if (!rec) return '';

  for (const key of keys) {
    const v = rec[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }

  return '';
}

function getNestedRecord(obj: unknown, key: string): Record<string, unknown> | null {
  const rec = asRecord(obj);
  if (!rec) return null;
  return asRecord(rec[key]);
}

function extractCustomerPrefill(session: TechDiagnosticSession | null) {
  const jobDetails = session?.job_details || {};

  const customer =
    getNestedRecord(jobDetails, 'customer') ||
    getNestedRecord(jobDetails, 'Customer') ||
    getNestedRecord(jobDetails, 'customerInfo') ||
    null;

  const contact =
    getNestedRecord(jobDetails, 'contact') ||
    getNestedRecord(jobDetails, 'Contact') ||
    getNestedRecord(jobDetails, 'primary_contact') ||
    null;

  const location =
    getNestedRecord(jobDetails, 'location') ||
    getNestedRecord(jobDetails, 'Location') ||
    getNestedRecord(jobDetails, 'service_location') ||
    null;

  const email =
    getStringFrom(jobDetails, 'customer_email', 'email') ||
    getStringFrom(contact, 'email', 'Email') ||
    getStringFrom(customer, 'email', 'Email');

  const firstName =
    getStringFrom(jobDetails, 'customer_first_name', 'first_name', 'firstName') ||
    getStringFrom(customer, 'first_name', 'firstName', 'FirstName');

  const lastName =
    getStringFrom(jobDetails, 'customer_last_name', 'last_name', 'lastName') ||
    getStringFrom(customer, 'last_name', 'lastName', 'LastName');

  const phone =
    getStringFrom(jobDetails, 'customer_phone', 'phone') ||
    getStringFrom(contact, 'phone', 'phoneNumber', 'phone_number');

  const address1 = getStringFrom(location, 'address1', 'address_1', 'streetAddress', 'street_address', 'Address1', 'Address');
  const city = getStringFrom(location, 'city', 'City');
  const state = getStringFrom(location, 'state', 'State');
  const zip = getStringFrom(location, 'zip', 'zipCode', 'zip_code', 'Zip');

  return {
    email,
    firstName,
    lastName,
    phone,
    address1,
    city,
    state,
    zip,
  };
}

function resolveSelectedOption(session: TechDiagnosticSession | null): SelectedOptionSummary | null {
  if (!session) return null;

  const optId = normalizeOptionId(session.option_selected);
  if (!optId) return null;

  const optionsRaw = (session.options_presented || {}) as Record<string, unknown>;
  const raw = asRecord(optionsRaw[optId]) || {};

  const title = String(raw.title || '').trim();
  const totalDueTodayUsd = toNumber(raw.total_due_today_usd ?? raw.total_usd ?? raw.price_usd);
  const membershipFirstPaymentUsd = toNumber(raw.membership_first_payment_usd);
  const notes = String(raw.notes || '').trim();

  return {
    id: optId,
    title: title || optId.replace('_', ' ').toUpperCase(),
    totalDueTodayUsd,
    membershipFirstPaymentUsd,
    notes,
  };
}

function toLines(text: string): string[] {
  return String(text || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

export default function TechDiagnosticInvoicePage() {
  const { id } = useParams();
  const sessionId = String(id || '').trim();

  const navigate = useNavigate();
  const { accessToken } = useTechAuth();

  const [session, setSession] = useState<TechDiagnosticSession | null>(null);
  const [invoice, setInvoice] = useState<TechDiagnosticInvoice | null>(null);

  const [loading, setLoading] = useState(false);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Payment form
  const paymentFormRef = useRef<HTMLFormElement>(null);
  const { isStaxReady, cardFormStatus, staxError, setBrandKey, tokenizePaymentMethod } = useStax();

  const [cardMonth, setCardMonth] = useState('');
  const [cardYear, setCardYear] = useState('');

  // Customer details (for receipt + tokenization)
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerFirstName, setCustomerFirstName] = useState('');
  const [customerLastName, setCustomerLastName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');

  const [billingAddress1, setBillingAddress1] = useState('');
  const [billingCity, setBillingCity] = useState('');
  const [billingState, setBillingState] = useState('FL');
  const [billingZip, setBillingZip] = useState('');

  const selectedOption = useMemo(() => resolveSelectedOption(session), [session]);
  const optionNotes = useMemo(() => toLines(selectedOption?.notes || ''), [selectedOption?.notes]);

  const load = async () => {
    if (!accessToken) return;
    if (!sessionId) return;

    setLoading(true);
    setError(null);
    try {
      const s = await getTechDiagnosticSession(accessToken, sessionId);
      setSession(s);

      setBrandKey(s.company_id);

      const inv = await createTechDiagnosticInvoice(accessToken, sessionId);
      setInvoice(inv);

      const prefill = extractCustomerPrefill(s);

      setCustomerEmail((prev) => prev || prefill.email);
      setCustomerFirstName((prev) => prev || prefill.firstName);
      setCustomerLastName((prev) => prev || prefill.lastName);
      setCustomerPhone((prev) => prev || prefill.phone);

      setBillingAddress1((prev) => prev || prefill.address1);
      setBillingCity((prev) => prev || prefill.city);
      setBillingState((prev) => prev || prefill.state || 'FL');
      setBillingZip((prev) => prev || prefill.zip);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, sessionId]);

  const dueToday = invoice?.amount_due_today_cents ?? 0;

  const handlePay = async () => {
    setError(null);

    if (!accessToken) return;
    if (!sessionId) return;
    if (!session || !invoice) {
      setError('Invoice is not ready yet.');
      return;
    }

    if (invoice.status === 'paid') {
      setError('This invoice is already paid.');
      return;
    }

    if (!customerEmail.trim()) {
      setError('Customer email is required for the receipt.');
      return;
    }

    if (!customerFirstName.trim() || !customerLastName.trim()) {
      setError('Customer first and last name are required.');
      return;
    }

    if (!isStaxReady || cardFormStatus !== 'mounted') {
      setError('Payment system is still loading. Please wait a moment and try again.');
      return;
    }

    if (!cardMonth || !cardYear) {
      setError('Enter card expiration month and year.');
      return;
    }

    if (!paymentFormRef.current) {
      setError('Payment form is not ready. Please refresh and try again.');
      return;
    }

    setPaying(true);
    try {
      setBrandKey(session.company_id);

      const paymentToken = await tokenizePaymentMethod({
        form: paymentFormRef.current,
        extraDetails: {
          firstname: customerFirstName.trim(),
          lastname: customerLastName.trim(),
          email: customerEmail.trim(),
          phone: customerPhone.trim() || undefined,
          address_1: billingAddress1.trim() || undefined,
          address_city: billingCity.trim() || undefined,
          address_state: billingState.trim() || undefined,
          address_zip: billingZip.trim() || undefined,
        },
        brandKey: session.company_id,
      });

      const result = await payTechDiagnosticInvoice(accessToken, sessionId, {
        payment_token: paymentToken,
        customer: {
          email: customerEmail.trim(),
          first_name: customerFirstName.trim(),
          last_name: customerLastName.trim(),
          phone: customerPhone.trim() || undefined,
          address_1: billingAddress1.trim() || undefined,
          address_city: billingCity.trim() || undefined,
          address_state: billingState.trim() || undefined,
          address_zip: billingZip.trim() || undefined,
        },
      });

      setInvoice(result.invoice);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setPaying(false);
    }
  };

  if (!sessionId) {
    return (
      <div className="p-6 bg-white border border-gray-200 rounded-2xl">
        <div className="text-sm text-red-800 font-medium">Missing diagnostic session id.</div>
      </div>
    );
  }

  const paid = invoice?.status === 'paid';

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(`/diagnostics/${encodeURIComponent(sessionId)}/presentation`)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-gray-200 hover:bg-gray-50"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Invoice & Payment</h1>
          </div>
          <p className="text-gray-600 mt-2">
            Session: <span className="font-mono text-xs">{sessionId}</span>
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-gray-200 hover:bg-gray-50"
            disabled={loading}
          >
            <RefreshCw className={['w-4 h-4', loading ? 'animate-spin' : ''].join(' ')} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-2xl">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800 font-medium">{error}</p>
          </div>
        </div>
      )}

      {paid && (
        <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-700 flex-shrink-0" />
            <p className="text-sm text-green-900 font-semibold">
              Payment complete. Receipt will be emailed to <span className="font-mono">{customerEmail || invoice?.customer_email || '—'}</span>.
            </p>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-2xl p-6 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Summary</h2>
            <div className="text-sm text-gray-600">Amount due today: <span className="font-semibold">{formatUsdFromCents(dueToday)}</span></div>
          </div>
          <div className="text-xs text-gray-500">
            Status: <span className="font-mono">{invoice?.status || '—'}</span>
          </div>
        </div>

        {selectedOption ? (
          <div className="border border-gray-200 rounded-xl p-4">
            <div className="text-xs text-gray-500">Accepted option</div>
            <div className="mt-1 text-lg font-semibold text-gray-900">{selectedOption.title}</div>
            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="bg-gray-50 rounded-xl p-3">
                <div className="text-xs text-gray-500">Work total due today</div>
                <div className="mt-1 text-xl font-bold text-gray-900">{formatUsd(selectedOption.totalDueTodayUsd)}</div>
              </div>
              <div className="bg-gray-50 rounded-xl p-3">
                <div className="text-xs text-gray-500">Membership first payment (info)</div>
                <div className="mt-1 text-xl font-bold text-gray-900">{formatUsd(selectedOption.membershipFirstPaymentUsd)}</div>
              </div>
            </div>

            {optionNotes.length ? (
              <div className="mt-3 bg-gray-50 rounded-xl p-3">
                <div className="text-xs text-gray-500">What’s included</div>
                <ul className="mt-2 text-sm text-gray-800 list-disc pl-5 space-y-1">
                  {optionNotes.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3">
            No accepted option found yet. Confirm an option in Presentation Mode first.
          </div>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl p-6 space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">Customer receipt details</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="email">Customer email (required)</Label>
            <Input id="email" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="phone">Phone (optional)</Label>
            <Input id="phone" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="firstName">First name (required)</Label>
            <Input id="firstName" value={customerFirstName} onChange={(e) => setCustomerFirstName(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="lastName">Last name (required)</Label>
            <Input id="lastName" value={customerLastName} onChange={(e) => setCustomerLastName(e.target.value)} />
          </div>
        </div>

        <div>
          <Label htmlFor="address1">Billing address (recommended)</Label>
          <Input id="address1" value={billingAddress1} onChange={(e) => setBillingAddress1(e.target.value)} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label htmlFor="city">City</Label>
            <Input id="city" value={billingCity} onChange={(e) => setBillingCity(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="state">State</Label>
            <Input id="state" value={billingState} onChange={(e) => setBillingState(e.target.value)} maxLength={2} />
          </div>
          <div>
            <Label htmlFor="zip">ZIP</Label>
            <Input id="zip" value={billingZip} onChange={(e) => setBillingZip(e.target.value)} />
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl p-6 space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">Payment</h2>

        <form ref={paymentFormRef} className="space-y-3" onSubmit={(e) => e.preventDefault()}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <Label>Card number</Label>
              <div id="card-number" className="h-10 px-3 border border-gray-300 rounded-md bg-white flex items-center" />
            </div>
            <div>
              <Label>CVV</Label>
              <div id="card-cvv" className="h-10 px-3 border border-gray-300 rounded-md bg-white flex items-center" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label htmlFor="month">Exp Month</Label>
              <Input
                id="month"
                name="month"
                placeholder="MM"
                value={cardMonth}
                onChange={(e) => setCardMonth(e.target.value.slice(0, 2))}
                inputMode="numeric"
                maxLength={2}
              />
            </div>
            <div>
              <Label htmlFor="year">Exp Year</Label>
              <Input
                id="year"
                name="year"
                placeholder="YYYY"
                value={cardYear}
                onChange={(e) => setCardYear(e.target.value.slice(0, 4))}
                inputMode="numeric"
                maxLength={4}
              />
            </div>
            <div className="text-xs text-gray-500 flex items-center">
              {cardFormStatus === 'loading' && 'Loading secure fields…'}
              {cardFormStatus === 'error' && staxError && <span className="text-red-500">{staxError}</span>}
              {cardFormStatus === 'mounted' && <span className="text-green-600">Payment form ready.</span>}
              {!isStaxReady && 'Loading payment system…'}
            </div>
          </div>
        </form>

        <div className="pt-4 border-t flex justify-end">
          <Button variant="primary" disabled={paying || loading || paid || dueToday <= 0} onClick={handlePay}>
            {paid ? 'Paid' : paying ? 'Processing…' : `Charge ${formatUsdFromCents(dueToday)}`}
          </Button>
        </div>
      </div>
    </div>
  );
}
